import { Injectable, inject } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { compose, patch, updateItem } from '@ngxs/store/operators';
import { User } from '@wilson/interfaces';
import {
  catchError,
  filter,
  finalize,
  Observable,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { AccountService } from '../account.service';
import {
  FetchAllUsersIfNeeded,
  RefreshUsers,
  ResetUsersState,
  UpdateMultipleUsers,
  UpdateSingleUser,
} from './users-state.action';

export interface UsersStateModel {
  users: User[];
  loading: boolean;
  isUsersStateInitialized: boolean;
}

const defaultUsersState: UsersStateModel = {
  users: [],
  loading: false,
  isUsersStateInitialized: false,
};

@State<UsersStateModel>({
  name: 'UsersState',
  defaults: defaultUsersState,
})
@Injectable()
export class UsersState {
  private readonly accountService = inject(AccountService);

  static ensureUsersLoaded(store: Store): Observable<boolean> {
    return store.dispatch(new FetchAllUsersIfNeeded()).pipe(
      switchMap(() => store.select(UsersState.isUsersStateInitialized)),
      filter((isInitialized) => isInitialized),
      take(1),
    );
  }

  @Selector()
  static isLoading(state: UsersStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static users(state: UsersStateModel): User[] {
    return state.users;
  }

  @Selector()
  static usersMap(state: UsersStateModel): Record<string, User> {
    return state.users.reduce(
      (acc, user) => {
        acc[user.id as string] = user;
        return acc;
      },
      {} as Record<string, User>,
    );
  }

  @Selector()
  static isUsersStateInitialized(state: UsersStateModel): boolean {
    return state.isUsersStateInitialized;
  }
  static userById(
    userId: string,
  ): (state: UsersStateModel) => User | undefined {
    return createSelector([UsersState], (state: UsersStateModel) => {
      return state.users.find((user) => user.id === userId);
    });
  }

  static userByIdExists(userId: string): (state: UsersStateModel) => boolean {
    return createSelector([UsersState], (state: UsersStateModel) => {
      return state.users.some((user) => user.id === userId);
    });
  }

  @Selector()
  static masterDeciderUserIds(state: UsersStateModel): string[] {
    return state.users
      .filter((user) => user.isAbsenceMasterDecider)
      .map((user) => user.id as string);
  }

  @Action(ResetUsersState)
  resetUsersState({ setState }: StateContext<UsersStateModel>): void {
    setState(defaultUsersState);
  }

  @Action(FetchAllUsersIfNeeded)
  fetchAllUsers({
    getState,
    setState,
  }: StateContext<UsersStateModel>): Observable<User[]> | undefined {
    const state = getState();
    if (!state.isUsersStateInitialized && !state.loading) {
      setState(
        patch<UsersStateModel>({
          loading: true,
        }),
      );

      return this.accountService.getUsers().pipe(
        catchError((error) => {
          setState(
            patch<UsersStateModel>({
              isUsersStateInitialized: false,
            }),
          );
          return throwError(() => error);
        }),
        tap((users) => {
          setState(
            patch<UsersStateModel>({
              users,
              isUsersStateInitialized: true,
            }),
          );
        }),
        finalize(() =>
          setState(
            patch<UsersStateModel>({
              loading: false,
            }),
          ),
        ),
      );
    } else {
      return undefined;
    }
  }

  @Action(RefreshUsers)
  refreshUsers({
    setState,
  }: StateContext<UsersStateModel>): Observable<User[]> {
    setState(
      patch<UsersStateModel>({
        loading: true,
      }),
    );
    return this.accountService.getUsers().pipe(
      tap((users) => {
        setState(
          patch<UsersStateModel>({
            users,
            isUsersStateInitialized: true,
          }),
        );
      }),
      finalize(() =>
        setState(
          patch<UsersStateModel>({
            loading: false,
          }),
        ),
      ),
    );
  }

  @Action(UpdateSingleUser)
  updateSingleUser(
    { setState }: StateContext<UsersStateModel>,
    action: UpdateSingleUser,
  ): void {
    setState(
      patch({
        users: updateItem<User>(
          (user) => user.id === action.user.id,
          patch(action.user),
        ),
      }),
    );
  }

  @Action(UpdateMultipleUsers)
  updateMultipleUsers(
    { setState }: StateContext<UsersStateModel>,
    action: UpdateMultipleUsers,
  ): void {
    const updateInstructions = action.users.map((userUpdate) =>
      updateItem<User>((user) => user.id === userUpdate.id, patch(userUpdate)),
    );

    setState(
      patch({
        users: compose(...updateInstructions),
      }),
    );
  }
}
